import { enqueueSnackbar } from 'notistack';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { IconEye, IconEyeOff } from '@tabler/icons-react';

import Button from '@/components/Button/Button';
import Input from '@/components/Input/Input';
import { getFCMToken } from '@/firebase/fcm';
import { useFormatText } from '@/hooks/useFormatText';
import { TLoginForm, loginFormSchema } from '@/schema/loginSchema';
import { LoginApi } from '@/service/authService';
import { useSettingsStore } from '@/store/useSettingsStore';
import useUser from '@/store/useUser';
import { IUser } from '@/types';
import { generateOrGetUserId } from '@/utils';
import { encryptPassword } from '@/utils/encryption';
import { zodResolver } from '@hookform/resolvers/zod';

const Login = () => {
  const navigate = useNavigate();
  const setUser = useUser((state) => state.setUser);
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const { updateSettings: updateStore } = useSettingsStore();

  const { formatText } = useFormatText();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<TLoginForm>({
    resolver: zodResolver(loginFormSchema),
    mode: 'onChange',
  });

  const watchedFields = watch(['email', 'password']);
  const [email, password] = watchedFields;

  const onFinish = async () => {
    if (loading) return;
    const encryptedPassword = encryptPassword(password);
    setLoading(true);
    try {
      let fcmToken: string | undefined;
      try {
        fcmToken = await getFCMToken();
      } catch (err) {
        console.warn('FCM token not available:', err);
      }
      const data: IUser = {
        email: email,
        password: encryptedPassword,
        device_id: generateOrGetUserId(),
        fcm_token: fcmToken,
      };

      const response = await LoginApi(data);

      if (response?.data?.status) {
        enqueueSnackbar(response?.data?.message, { variant: 'success' });
        setUser({
          token: response?.data?.data?.token,
          id: response?.data?.data?.admin?.id,
          name: response?.data?.data?.admin?.username,
          email: response?.data?.data?.admin?.email,
          is_access: response?.data?.data?.admin?.role?.access_level,
          sections: response?.data?.data?.admin?.role?.sections,
          profile_image: response?.data?.data?.admin?.profile_pic,
        });

        const settings = response?.data?.data?.admin?.settings;
        updateStore({
          language: settings?.language,
          direction: settings?.language === 'ar' ? 'rtl' : 'ltr',
          dateFormat: settings?.date_format,
          currency: settings?.currency,
        });
        navigate('/');
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col justify-center p-15 md:p-10 w-full lg:max-w-150">
      <div className="w-full flex flex-col items-center">
        <div className="text-center mb-10">
          <p className="pb-2 text-4xl max-md:text-3xl font-bold text-neutral-primary tracking-[-0.36px] leading-11">
            {formatText('login.title')}
          </p>
          <p className="text-neutral-secondary text-lg leading-7">
            {formatText('login.sub-title')}
          </p>
        </div>

        <form
          method="POST"
          className="w-full"
          onSubmit={handleSubmit(onFinish)}
          autoComplete="off"
        >
          <div className="flex flex-col gap-4">
            <Input
              label={formatText('login.form.email')}
              placeholder={formatText('login.form.email-placeholder')}
              name="email"
              type="email"
              isRequired
              register={register}
              autoComplete="username"
              error={
                errors.email?.message
                  ? formatText(errors.email.message)
                  : undefined
              }
            />

            <Input
              label={formatText('login.form.password')}
              name="password"
              type={show ? 'text' : 'password'}
              placeholder={formatText('login.form.password-placeholder')}
              register={register}
              isRequired
              autoComplete="current-password"
              data-lpignore="true"
              data-form-type="other"
              rightIcon={{
                icon: show ? (
                  <IconEyeOff
                    size={20}
                    color="#99A0AE"
                    className="cursor-pointer"
                  />
                ) : (
                  <IconEye
                    size={20}
                    color="#99A0AE"
                    className="cursor-pointer"
                  />
                ),
                onClick: () => setShow((prev) => !prev),
              }}
              error={
                errors.password?.message
                  ? formatText(errors.password.message)
                  : undefined
              }
              rightIconBg={'#ffffff'}
            />
          </div>
          <div className="flex w-full justify-end items-center mt-4 mb-6">
            <Button
              variant="link"
              href="/forgot-password"
              label={formatText('login.form.actions.forgot-password')}
              className="text-neutral-primary"
            >
              {formatText('login.form.actions.forgot-password')}
            </Button>
          </div>

          <Button
            label={formatText('login.form.actions.login')}
            type="submit"
            variant="primary"
            loading={loading}
            className="w-full py-3.5! leading-5"
          />
        </form>
      </div>
    </div>
  );
};

export default Login;
